import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { assignmentsAPI } from '../api/assignments'
import type { SurveyAssignment } from '../api/assignments'
import { organizationsAPI } from '../api/organizations'
import type { OrganizationMember } from '../api/organizations'
import { useAuth } from '../contexts/AuthContext'
import { Button, Card, CardHeader, StatusBadge, Alert } from './ui'

interface AssignmentSectionProps {
  surveyId: string
  orgId: string
}

export const AssignmentSection: React.FC<AssignmentSectionProps> = ({ surveyId, orgId }) => {
  const intl = useIntl()
  const { user } = useAuth()
  const [assignments, setAssignments] = useState<SurveyAssignment[]>([])
  const [members, setMembers] = useState<OrganizationMember[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [assignAll, setAssignAll] = useState(false)
  const [loading, setLoading] = useState(true)
  const [assigning, setAssigning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isOwnerOrAdmin, setIsOwnerOrAdmin] = useState(false)

  useEffect(() => {
    loadData()
  }, [surveyId, orgId])

  const loadData = async () => {
    try {
      setLoading(true)
      const [assignmentList, memberList] = await Promise.all([
        assignmentsAPI.getAssignments(orgId, surveyId),
        organizationsAPI.getMembers(orgId),
      ])
      setAssignments(assignmentList)
      setMembers(memberList)

      const currentMember = memberList.find((m) => m.user.email === user?.email)
      setIsOwnerOrAdmin(currentMember?.role === 'OWNER' || currentMember?.role === 'ADMIN')
    } catch (err: unknown) {
      console.error('Failed to load assignments:', err)
    } finally {
      setLoading(false)
    }
  }

  const assignedMemberIds = new Set(assignments.map((a) => a.member_id))
  const unassignedMembers = members.filter((m) => !assignedMemberIds.has(m.user.id))

  const handleAssignAllChange = (checked: boolean) => {
    setAssignAll(checked)
    if (checked) {
      setSelectedIds(unassignedMembers.map((m) => m.user.id))
    } else {
      setSelectedIds([])
    }
  }

  const handleToggleMember = (userId: string) => {
    setSelectedIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    )
  }

  const handleAssign = async () => {
    if (selectedIds.length === 0) return
    try {
      setAssigning(true)
      setError(null)
      await assignmentsAPI.assignMembers(orgId, surveyId, selectedIds)
      setSelectedIds([])
      setAssignAll(false)
      await loadData()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : intl.formatMessage({ id: 'assignments.error.assign' })
      setError(message)
    } finally {
      setAssigning(false)
    }
  }

  const handleUnassign = async (memberId: string) => {
    if (!confirm(intl.formatMessage({ id: 'assignments.confirm.unassign' }))) return
    try {
      await assignmentsAPI.unassignMember(orgId, surveyId, memberId)
      await loadData()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : intl.formatMessage({ id: 'assignments.error.unassign' })
      alert(message)
    }
  }

  if (loading) {
    return (
      <Card elevated className="mt-6">
        <div className="text-center py-4 text-foreground-muted"><FormattedMessage id="assignments.loading" /></div>
      </Card>
    )
  }

  return (
    <Card elevated className="mt-6">
      <CardHeader
        title={intl.formatMessage({ id: 'assignments.title' }, { count: assignments.length })}
        subtitle={intl.formatMessage({ id: 'assignments.subtitle' })}
      />

      {error && <Alert variant="error" className="mb-4">{error}</Alert>}

      {/* Assign members picker — only for OWNER/ADMIN */}
      {isOwnerOrAdmin && unassignedMembers.length > 0 && (
        <div className="mb-6 p-4 bg-background-alt rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              id="assign-all"
              checked={assignAll}
              onChange={(e) => handleAssignAllChange(e.target.checked)}
              className="rounded border-border text-primary focus:ring-primary"
            />
            <label htmlFor="assign-all" className="text-sm font-medium text-foreground">
              <FormattedMessage id="assignments.assignAll" values={{ count: unassignedMembers.length }} />
            </label>
          </div>

          {!assignAll && (
            <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
              {unassignedMembers.map((m) => (
                <label key={m.user.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(m.user.id)}
                    onChange={() => handleToggleMember(m.user.id)}
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-foreground">{m.user.full_name || m.user.email}</span>
                  <span className="text-foreground-muted">({m.user.email})</span>
                </label>
              ))}
            </div>
          )}

          <Button
            size="sm"
            onClick={handleAssign}
            loading={assigning}
            disabled={selectedIds.length === 0}
          >
            <FormattedMessage id="assignments.assignButton" values={{ count: selectedIds.length }} />
          </Button>
        </div>
      )}

      {/* Assignment table */}
      {assignments.length === 0 ? (
        <div className="text-center py-4 text-foreground-muted">
          <FormattedMessage id="assignments.empty" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="assignments.col.name" /></th>
                <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="assignments.col.email" /></th>
                <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="assignments.col.status" /></th>
                <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="assignments.col.assigned" /></th>
                {isOwnerOrAdmin && (
                  <th className="pb-3 font-semibold text-foreground text-right"><FormattedMessage id="assignments.col.actions" /></th>
                )}
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a.id} className="border-b border-border last:border-0">
                  <td className="py-3 font-medium text-foreground">{a.member_name || '—'}</td>
                  <td className="py-3 text-foreground-muted">{a.member_email}</td>
                  <td className="py-3">
                    <StatusBadge status={a.status} />
                  </td>
                  <td className="py-3 text-foreground-muted">
                    {new Date(a.assigned_at).toLocaleDateString()}
                  </td>
                  {isOwnerOrAdmin && (
                    <td className="py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnassign(a.member_id)}
                        className="text-error hover:text-error"
                      >
                        <FormattedMessage id="assignments.unassign" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
